import {useQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {useAnalytics} from '@github-ui/use-analytics'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {NavList, Spinner} from '@primer/react'

import {
  buildQueryParams,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_MAX_PAGES,
  type DynamicMenuRepoItemProps,
  DynamicSidePanelItems,
  DynamicSidePanelItemSchema,
  useSidePanelItemsQuery,
} from './DynamicSidePanelItems'
import {TeamsBanner} from './TeamsBanner'

export const RICH_CONTENT_DEFAULT_PATH = '/_side-panels/global.json'
export const REPOS_SEARCH_PATH = '/_side-panel-items/global/repositories'

export interface DynamicSidePanelContentProps {
  richContentUrl?: string
  repositoriesSearchUrl?: string
  itemsPerPage?: number
  additionalRepositoryParams?: {
    initialItemsPerPage?: number
    maxPages?: number
  }
}

const RichTextDataSchema = Type.Object({
  repositories: Type.Optional(Type.Array(DynamicSidePanelItemSchema)),
  show_teams_banner: Type.Optional(Type.Boolean()),
})

const RichTextDataValidator = Compile(RichTextDataSchema)

export type RichContentData = Type.Static<typeof RichTextDataSchema>

export function useRichContent(props: DynamicSidePanelContentProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const queryUrl = props.richContentUrl ?? RICH_CONTENT_DEFAULT_PATH
  const itemsPerPage = props.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE
  const maxPages = props.additionalRepositoryParams?.maxPages ?? DEFAULT_MAX_PAGES

  const query = useQuery({
    queryKey: [
      'global-nav-side-panel',
      queryUrl,
      itemsPerPage,
      maxPages,
      props.additionalRepositoryParams,
      props.additionalRepositoryParams?.initialItemsPerPage,
    ],
    queryFn: async () => {
      if (!queryUrl) return null

      const queryParams = buildQueryParams({
        items_per_page: itemsPerPage.toString(),
        initial_items_per_page: props.additionalRepositoryParams?.initialItemsPerPage,
        max_pages: maxPages.toString(),
      })

      const response = await reactFetchJSON(`${queryUrl}${queryParams}`)
      if (!response.ok) throw new Error('Failed to fetch')
      return response.json() as Promise<RichContentData>
    },
    persister: createPersister({
      validator: RichTextDataValidator,
      sendAnalyticsEvent,
    }),
  })

  return query
}

export function DynamicSidePanelContent(props: DynamicSidePanelContentProps) {
  const {data, isLoading, error} = useRichContent(props)

  if (error || isLoading || !data) {
    return (
      <NavList.Group className="d-flex flex-justify-center width-full tmp-my-3">
        <Spinner data-testid="dynamic-side-content-loader" size="small" />
      </NavList.Group>
    )
  }

  return (
    <>
      {data.repositories && data.repositories.length > 0 && (
        <TopRepos initialItems={data.repositories} searchUrl={props.repositoriesSearchUrl ?? REPOS_SEARCH_PATH} />
      )}
      {data.show_teams_banner !== undefined && <TeamsBanner showTeamsBanner={data.show_teams_banner} />}
    </>
  )
}

export function TopRepos(props: DynamicMenuRepoItemProps) {
  const {fetchNextPage, hasNextPage, isLoading, data, error} = useSidePanelItemsQuery(props)

  if (error) {
    return null
  }

  return (
    <DynamicSidePanelItems
      title="Top repositories"
      items={data.pages}
      searchLabel="Search for repositories"
      itemsPerPage={props.itemsPerPage}
      initialItemsPerPage={props.initialItemsPerPage}
      maxPages={props.maxPages}
      searchUrl={props.searchUrl}
      isLoading={isLoading}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      analytics={{
        subCategory: 'REPOSITORIES',
        showMoreAction: 'SHOW_MORE_REPOS',
        itemAction: 'REPOSITORY_ITEM',
      }}
    />
  )
}
